"use client"

import { useCallback } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { useCatalogTransition } from "@/components/public/CatalogTransitionContext"
import { useTranslations } from "@/lib/i18n/client"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"

interface CatalogPaginationProps {
  currentPage: number
  totalPages: number
}

export function CatalogPagination({
  currentPage,
  totalPages,
}: CatalogPaginationProps) {
  const { t } = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { startTransition } = useCatalogTransition()

  const getPageUrl = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams.toString())
      if (page <= 1) {
        params.delete("pagina")
      } else {
        params.set("pagina", String(page))
      }
      return `/aeronaves?${params.toString()}`
    },
    [searchParams],
  )

  const handlePageClick = useCallback(
    (page: number) => (e: React.MouseEvent) => {
      e.preventDefault()
      startTransition(() => {
        router.push(getPageUrl(page))
      })
      window.scrollTo({ top: 0, behavior: "smooth" })
    },
    [router, getPageUrl, startTransition],
  )

  if (totalPages <= 1) return null

  // Build page numbers with ellipsis
  const pages: (number | "ellipsis")[] = []
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i)
  } else {
    pages.push(1)
    if (currentPage > 3) pages.push("ellipsis")
    const rangeStart = Math.max(2, currentPage - 1)
    const rangeEnd = Math.min(totalPages - 1, currentPage + 1)
    for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i)
    if (currentPage < totalPages - 2) pages.push("ellipsis")
    pages.push(totalPages)
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <Pagination>
        <PaginationContent>
          {currentPage > 1 && (
            <PaginationItem>
              <PaginationPrevious
                text={t.catalog.previous}
                href={getPageUrl(currentPage - 1)}
                onClick={handlePageClick(currentPage - 1)}
              />
            </PaginationItem>
          )}

          {pages.map((page, i) =>
            page === "ellipsis" ? (
              <PaginationItem key={`ellipsis-${i}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={page}>
                <PaginationLink
                  href={getPageUrl(page)}
                  isActive={page === currentPage}
                  onClick={handlePageClick(page)}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ),
          )}

          {currentPage < totalPages && (
            <PaginationItem>
              <PaginationNext
                text={t.catalog.next}
                href={getPageUrl(currentPage + 1)}
                onClick={handlePageClick(currentPage + 1)}
              />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  )
}
